import React from 'react';
import ReactECharts from 'components/ThemedECharts';
import { SequentialGraphData } from 'types/Meteorology/Agro/WaterBalanceSequential/DetailedView/getGraph';
import GRAPH_CONFIG_MAP from 'helper/Meteorology/Agro/WaterBalanceCulture/DetailedView/graphConfig';
import { MEDIUM_BLUE } from 'constants/colors';
import { applyChartSubtitle } from 'helper/Meteorology/Agro/chartSubtitle';
import { getDataZoomConfig, getXAxisConfig } from 'components/Meteorology/Agro/WaterBalanceShared/chartConfig';
import CombinedChart from './CombinedChart';
import ChartContainer from './ChartContainer';

export default function ChartDisplay({
  zoomStart,
  zoomEnd,
  graphData,
  combinedData,
  height = '30rem',
  hideTitle = false,
  hideDataZoom = false,
  subtitle = [],
}: {
  zoomStart: number;
  zoomEnd: number;
  graphData?: SequentialGraphData;
  combinedData?: {
    chuva: SequentialGraphData;
    temperatura: SequentialGraphData;
  };
  height?: string;
  hideTitle?: boolean;
  hideDataZoom?: boolean;
  subtitle?: string[];
}) {
  if (combinedData) {
    return (
      <CombinedChart
        zoomStart={zoomStart}
        zoomEnd={zoomEnd}
        chuva={combinedData.chuva}
        temperatura={combinedData.temperatura}
        height={height}
        hideTitle={hideTitle}
        hideDataZoom={hideDataZoom}
        subtitle={subtitle}
      />
    );
  }

  const graphName = graphData?.name?.toLowerCase();
  const graphConfig = graphName ? GRAPH_CONFIG_MAP[graphName] : undefined;

  const isARM = graphName === 'arm';
  const isDefXExc = graphName === 'def_x_exc';

  return (
    <ChartContainer height={height}>
      <ReactECharts
        className="h-100"
        option={applyChartSubtitle({
          title: hideTitle ? undefined : {
            text: graphData?.label || '',
            left: 'center',
            top: 0,
          },
          legend: (isDefXExc && !hideTitle) ? {
            data: ['DEF', 'EXC'],
            top: 30,
            left: 'center',
          } : undefined,
          xAxis: getXAxisConfig(),
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: isARM ? '{value} %' : '{value} mm',
            },
          },
          series: isDefXExc ? [
            {
              name: 'DEF',
              data: graphData?.data.map((point) => ({
                name: point.date,
                value: point.value < 0 ? [point.date, point.value] : [point.date, null],
              })) || [],
              type: 'bar',
              itemStyle: {
                color: '#d32f2f', // tema: cor de dado
              },
            },
            {
              name: 'EXC',
              data: graphData?.data.map((point) => ({
                name: point.date,
                value: point.value >= 0 ? [point.date, point.value] : [point.date, null],
              })) || [],
              type: 'bar',
              itemStyle: {
                color: MEDIUM_BLUE, // tema: cor de dado
              },
            },
          ] : [
            {
              data:
                graphData?.data.map((point) => ({
                  name: point.date,
                  value: [point.date, point.value],
                  ...(graphConfig?.getItemStyle && {
                    itemStyle: graphConfig.getItemStyle(point.value),
                  }),
                })) || [],
              type: graphConfig?.type || 'line',
              smooth: graphConfig?.smooth ?? true,
              ...(graphConfig?.lineColor && {
                itemStyle: { color: graphConfig.lineColor },
                lineStyle: {
                  color: graphConfig.lineColor,
                  width: 2,
                },
              }),
            },
          ],
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              label: {
                formatter(params) {
                  const date = new Date(params.value);
                  return date.toLocaleDateString('pt-BR');
                },
              },
            },
          },
          dataZoom: getDataZoomConfig(zoomStart, zoomEnd, 0, hideDataZoom),
          animation: false,
        }, subtitle)}
      />
    </ChartContainer>
  );
}
